import { LightIcon } from "@xola/icons";
import { Alert, flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { getProductObject, handleSuccessfulSubmission, isExperienceProduct, isMembershipProduct } from "common";
import { filter, isEqual } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Link, useHistory } from "react-router-dom";
import { CatalogItemObject, FieldType, Form, PickupCatalogItem, Product } from "types";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { getIsGlobalFormNotOrganizerOnly } from "../GuestManagement/AttendeesSection";
import { QuestionnaireWarningModal } from "../GuestManagement/QuestionnaireWarningModal";
import { AddonQuestionAction, AddonQuestionDeletion, Questions } from "./Questions";
import { api } from "@/api/api";
import {
    getOptionalSourcesList,
    getSavedViewsForUpdate,
    useDeleteForm,
    useForm,
    useQuestionnairePreference,
    useSaveForm,
} from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { hasUniqueGuestIdentification, sanitizeExperiencePayload } from "@/models/Experience";
import { createNewForm, ensureMembershipEmailField, getFormPayload, isQuestionnaireSkipped } from "@/models/Form/Form";

interface ProductQuestionnaireProps {
    product: Product;
}

export const MEMBERSHIP_QUESTIONNAIRE_REQUIRED_TOOLTIP =
    "Name and Email questions are always required for a membership";

export const ProductQuestionnaire = ({ product }: ProductQuestionnaireProps) => {
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();

    const [originalForm, setOriginalForm] = useState<Form>();
    const { mutateAsync: saveOrUpdateForm, isLoading: isUpdating, isSuccess: isFormSaved } = useSaveForm();
    const { mutateAsync: deleteForm, isLoading: isDeleting, isSuccess: isFormDeleted } = useDeleteForm(originalForm);

    const { mutateAsync: addProductPickupLocation } = useMutation(async (payload: PickupCatalogItem) => {
        if (payload?.id) {
            return api.put<PickupCatalogItem>(`/catalogItems/${payload.id}`, payload);
        }

        return api.post<PickupCatalogItem>("/catalogItems", payload);
    });

    const { data: pickupLocations } = useAutoFetchCollection<PickupCatalogItem>(
        "catalogItems",
        { object: CatalogItemObject.PICKUP_CATALOG_ITEM },
        {},
    );

    const { form: globalForm } = useForm(undefined, product.object);

    const {
        initialForm,
        form,
        setForm,
        isLoading: isFormLoading,
        isGlobalFormExists,
    } = useForm(product.id, getProductObject(product));
    const { viewConfigurations, setPreference } = useQuestionnairePreference();
    const [isQuestionnaireSectionEnabled, setIsQuestionnaireSectionEnabled] = useState(false);

    const isExperience = isExperienceProduct(product);
    const isMembership = isMembershipProduct(product);

    useEffect(() => {
        setIsQuestionnaireSectionEnabled(isMembership || (initialForm?.fields?.length ?? 0) > 0);
    }, [initialForm, isMembership]);

    const isFormDataChanged = !isEqual(initialForm, form);
    const initialQuestionsToggleState = (initialForm?.fields?.length ?? 0) > 0;
    const isQuestionsToggleStateChanged = !!form && isQuestionnaireSectionEnabled !== initialQuestionsToggleState;
    const formChanged = isFormDataChanged || isQuestionsToggleStateChanged;
    const [pendingAddonDeletions, setPendingAddonDeletions] = useState<AddonQuestionDeletion[]>([]);

    const experience = isExperience ? product : undefined;

    const [isQuestionnaireWarningOpened, { toggle: toggleQuestionnaireWarningModal }] = useVisibility();
    const isAttendeeUnique = hasUniqueGuestIdentification(experience);

    const isGlobalFormNotOrganizerOnly = globalForm ? getIsGlobalFormNotOrganizerOnly(globalForm) : false;

    const {
        mutateAsync: saveExperience,
        isLoading: isSaving,
        isSuccess: isSaved,
    } = useMutation(async () => {
        await api.put(
            `/experiences/${experience?.id}`,
            experience ? sanitizeExperiencePayload(experience) : experience,
        );
    });

    const { mutateAsync: deleteCatalogItem } = useMutation(
        async ({ id, cascade }: { id: string; cascade: boolean }) => {
            const url = `/catalogItems/${id}${cascade ? "?cascade=true" : ""}`;
            return api.delete(url);
        },
    );

    if (isFormLoading) {
        return <Loader />;
    }

    const handleSaveQuestionnaireForSavedViews = async (form: Form) => {
        if (!viewConfigurations) {
            return;
        }

        const preferencesToUpdate = getSavedViewsForUpdate(form, viewConfigurations, product.id);
        setPreference((preference) => {
            preference.value.configurations = [...preferencesToUpdate];
        });
    };

    const updatePickupLocations = async (form: Form) => {
        const pickupField = form.fields.find((field) => field.type === FieldType.PICKUP);
        if (!pickupField?.options?.length) return form;

        const optionsWithOverrides = filter(pickupField.options, (opt: any) => opt?.localOverrides);
        if (optionsWithOverrides.length === 0) return form;

        const responses = await Promise.all(
            optionsWithOverrides.map(async (option: any) => {
                const productPickup = option.pickupLocation;
                const isProductPickup = productPickup?.object === CatalogItemObject.PICKUP_PRODUCT_CATALOG_ITEM;

                const parentId = productPickup?.parent?.id;
                const parent = parentId ? pickupLocations.find((p) => p.id === parentId) : undefined;

                const overrides = option.localOverrides || {};

                const sameArrivalOffset = parent?.arrivalOffset
                    ? parent.arrivalOffset === overrides.arrivalOffset
                    : overrides.arrivalOffset === false || Number.isNaN(overrides.arrivalOffset);

                const sameAsParent =
                    !!parent &&
                    sameArrivalOffset &&
                    isEqual(overrides?.prices?.perPerson, parent?.prices?.perPerson) &&
                    isEqual(overrides?.prices?.perGroup, parent?.prices?.perGroup);

                if (sameAsParent && isProductPickup) {
                    if (option.id) {
                        await api.delete(`/catalogItems/${option.id}?cascade=true`);
                    }
                    return { oldId: productPickup.id, newId: parent.id };
                }

                const payload: any = {
                    object: CatalogItemObject.PICKUP_PRODUCT_CATALOG_ITEM,
                    product: { id: form?.product?.id },
                    parent: { id: parent ? parent.id : productPickup?.parent?.id || productPickup?.id },
                    overrideParent: true,
                    arrivalOffset: overrides?.arrivalOffset,
                    pricePerPerson: { ...overrides?.prices?.perPerson },
                    pricePerGroup: { ...overrides?.prices?.perGroup },
                    seller: { id: form.seller.id },
                };

                if (isProductPickup && option.id) {
                    payload.id = option.id;
                }

                const { data } = await addProductPickupLocation(payload);

                return { oldId: productPickup.id, newId: data?.id };
            }),
        );

        const idMap = Object.fromEntries(responses.map(({ oldId, newId }) => [oldId, newId]));

        const updatedFields = form.fields.map((field) => {
            if (field.type !== FieldType.PICKUP) return field;

            const updatedOptions = field.options?.map((opt: any) => {
                const oldPickupId = opt.pickupLocation?.id;
                if (!oldPickupId || !idMap[oldPickupId]) return opt;

                const newId = idMap[oldPickupId];
                const isRevertedToParent = pickupLocations.some((p) => p.id === newId);

                return {
                    ...opt,
                    id: newId,
                    pickupLocation: {
                        ...opt.pickupLocation,
                        id: newId,
                        object: isRevertedToParent
                            ? CatalogItemObject.PICKUP_CATALOG_ITEM
                            : CatalogItemObject.PICKUP_PRODUCT_CATALOG_ITEM,
                    },
                };
            });

            return { ...field, options: updatedOptions };
        });

        return { ...form, fields: updatedFields };
    };

    const handleOverrideGlobalChange = (isEnabled: boolean) => {
        if (!isEnabled && isGlobalFormNotOrganizerOnly && !isAttendeeUnique) {
            toggleQuestionnaireWarningModal();
            return;
        }

        if (isEnabled) {
            const newForm = originalForm ?? createNewForm(seller, false, product, product.object);
            setForm(isMembership ? ensureMembershipEmailField(newForm) : newForm);
        } else {
            setOriginalForm(form);
            setForm(undefined);
        }
    };

    const handleCancelClick = () => {
        const object = pluralize(getProductObject(product));
        history.push(`/products/${object}`);
    };

    const handleSave = async () => {
        if (isExperience && product.customerNamePreference !== experience?.customerNamePreference) {
            await saveExperience();
        }

        for (const deletion of pendingAddonDeletions) {
            try {
                if (deletion.action === AddonQuestionAction.REMOVE_FROM_PRODUCT) {
                    await deleteCatalogItem({ id: deletion.catalogItemId, cascade: false });
                }
            } catch (error) {
                // Continue processing other deletions even if one fails
                console.error(`Failed to process addon deletion for ${deletion.catalogItemId}:`, error);
            }
        }

        setPendingAddonDeletions([]);

        if (form) {
            const formToSave = { ...form };
            if (!isQuestionnaireSectionEnabled) {
                formToSave.fields = [];
            }
            const updatedForm = await updatePickupLocations(formToSave);
            await saveOrUpdateForm(getFormPayload(updatedForm));
            await handleSaveQuestionnaireForSavedViews(updatedForm);
        } else {
            await deleteForm();
            setOriginalForm(undefined);
        }

        handleSuccessfulSubmission(() => {
            invalidate("forms");
            invalidate("catalogItems");
            invalidate("products", product?.id);
            invalidate("experiences", product?.id);

            flash.success("Successfully updated Questionnaire");
        });
    };

    const handleSkipRequiredFields = (value: boolean) => {
        if (form) {
            setForm({ ...form, optionalSourcesList: getOptionalSourcesList(form, value) });
        }
    };

    const enabledForm = !!form;
    const isLoading = isUpdating || isDeleting || isSaving;

    return (
        <ContentLayout isWide>
            <ConfirmLeaveModal isFormTouched={formChanged} />
            <QuestionnaireWarningModal
                isOpen={isQuestionnaireWarningOpened}
                onClose={toggleQuestionnaireWarningModal}
            />

            <h2>Customer Questionnaire</h2>
            <ContentLayout.Section title="General Preferences">
                <div className="space-y-4">
                    <ToggleQuestionnaireForm
                        isGlobalFormExists={isGlobalFormExists}
                        enabled={enabledForm}
                        product={product}
                        onChange={handleOverrideGlobalChange}
                    />

                    {enabledForm && (
                        <div className="flex flex-col space-y-4">
                            {!isMembership && (
                                <Switch.Group>
                                    <Switch
                                        isChecked={isQuestionnaireSectionEnabled}
                                        data-testid={`enable-questionnaire-for-${getProductObject(product)}-switch`}
                                        onChange={setIsQuestionnaireSectionEnabled}
                                    />
                                    <Switch.Label direction="right">
                                        Enable questionnaire for this {getProductObject(product)}
                                    </Switch.Label>
                                </Switch.Group>
                            )}
                            <Switch.Group>
                                <Switch isChecked={isQuestionnaireSkipped(form)} onChange={handleSkipRequiredFields} />
                                <Switch.Label direction="right">
                                    Skip required field validations for back office purchases
                                </Switch.Label>
                            </Switch.Group>
                        </div>
                    )}
                </div>
            </ContentLayout.Section>
            {form && isQuestionnaireSectionEnabled && (
                <ContentLayout.Section title="Customer Questions">
                    <Questions
                        form={form}
                        isAttendeeUnique={isAttendeeUnique}
                        isMembershipProduct={isMembership}
                        product={product}
                        onFormChange={setForm}
                        onAddonDeletionsChange={setPendingAddonDeletions}
                    />
                </ContentLayout.Section>
            )}
            <ContentLayout.Footer preferenceSidebar>
                <SubmitButton
                    isLoading={isLoading}
                    disabled={isLoading}
                    isSuccess={isFormDeleted || isFormSaved || isSaved}
                    onClick={handleSave}
                >
                    Save
                </SubmitButton>

                <SecondaryButton type="reset" onClick={handleCancelClick}>
                    Cancel
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};

interface OverrideGlobalQuestionnaireProps {
    enabled: boolean;
    isGlobalFormExists: boolean;
    product: Product;
    onChange: (value: boolean) => void;
}

const ToggleQuestionnaireForm = ({
    enabled,
    isGlobalFormExists,
    product,
    onChange,
}: OverrideGlobalQuestionnaireProps) => {
    const productType = getProductObject(product);
    const globalQuestionnairePath = getGlobalQuestionnairePath(product);

    if (!isGlobalFormExists) {
        const isMembership = isMembershipProduct(product);

        return (
            <Switch.Group>
                <Tooltip content={MEMBERSHIP_QUESTIONNAIRE_REQUIRED_TOOLTIP} disabled={!isMembership}>
                    <Switch
                        isChecked={enabled}
                        disabled={isMembership}
                        data-testid="product-enable-questionnaire-switch"
                        onChange={onChange}
                    />
                </Tooltip>
                <Switch.Label direction="right">Enable customer questionnaire</Switch.Label>
            </Switch.Group>
        );
    }

    return (
        <Alert color="primary">
            <div className="space-y-4 p-2 leading-lg text-gray-darker">
                <div className="space-y-1">
                    <Switch.Group>
                        <Switch
                            isChecked={enabled}
                            data-testid="product-override-global-questionnaire-switch"
                            onChange={onChange}
                        />
                        <Switch.Label direction="right">
                            <div className="space-x-2 font-bold">
                                <LightIcon />
                                <span>Override global questionnaire</span>
                            </div>
                        </Switch.Label>
                    </Switch.Group>
                    <p>
                        This {productType} is using the{" "}
                        <Link to={globalQuestionnairePath} className="font-bold text-blue underline">
                            Global Questionnaire
                        </Link>
                        . You can configure a custom questionnaire specific to this {productType} by overriding the
                        global settings below.
                    </p>
                </div>
            </div>
        </Alert>
    );
};

const getGlobalQuestionnairePath = (product: Product) => {
    const membershipSubPath = isMembershipProduct(product) ? "memberships/" : "";
    return `/products/${membershipSubPath}globalSettings/questionnaire`;
};
